/**
 * Importação de cronogramas do MS Project (e do Primavera via planilha).
 * Três entradas chegam ao mesmo formato neutro (ImportedTask):
 *  - PDF impresso pelo MS Project (tabela de entrada), lido pelas posições do texto;
 *  - XML do MS Project (lido no navegador);
 *  - planilha .xlsx/.csv com Id, Nome, Nível, Duração, Início, Término e Predecessoras.
 * buildImportPlan converte as tarefas em EAP (tarefas-resumo) e atividades (tarefas folha),
 * preservando as datas do arquivo no calendário de dias úteis do projeto.
 */
import { WorkCalendar } from './calendar.js';
import { runCpm, findCycle } from './cpm.js';
import { isISODate } from './dates.js';
/* ------------------------------------------------------------------ campos */
const n = (s) => Number(s.replace(/\./g, '').replace(',', '.'));
/** "4119 hrs", "116,5 hrs", "5 dias", "2 sems", "0 d", "3 edias", "1 mês", "30 min" → horas (dia = 8 h). */
export function parseDurationHours(raw, hoursPerDay = 8) {
    if (raw === null || raw === undefined)
        return null;
    if (typeof raw === 'number')
        return raw * hoursPerDay;
    const s = raw.trim().toLowerCase().replace(/\?$/, '').trim();
    if (!s)
        return null;
    const m = s.match(/^(-?\d+(?:[.,]\d+)?)\s*([a-zçêã]*)\.?$/);
    if (!m)
        return null;
    const v = Number(m[1].replace(',', '.'));
    const u = m[2].replace(/^e/, '');
    if (!u || /^(d|dia|dias|day|days|dy|dys)$/.test(u))
        return v * hoursPerDay;
    if (/^(h|hr|hrs|hora|horas|hour|hours)$/.test(u))
        return v;
    if (/^(s|sem|sems|semana|semanas|w|wk|wks|week|weeks)$/.test(u))
        return v * hoursPerDay * 5;
    if (/^(ms|m[eê]s|meses|mo|mon|mons|month|months)$/.test(u))
        return v * hoursPerDay * 20;
    if (/^(m|min|mins|minuto|minutos|minute|minutes)$/.test(u))
        return v / 60;
    return null;
}
/** "19/10/26 09:00", "Seg 19/10/26", "2026-10-19T09:00:00", objeto Date → "AAAA-MM-DD HH:mm". */
export function parseDateTime(raw) {
    if (!raw)
        return null;
    if (raw instanceof Date) {
        const p = (x) => String(x).padStart(2, '0');
        return `${raw.getUTCFullYear()}-${p(raw.getUTCMonth() + 1)}-${p(raw.getUTCDate())} ${p(raw.getUTCHours())}:${p(raw.getUTCMinutes())}`;
    }
    const s = raw.trim();
    let m = s.match(/(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{1,2}):(\d{2}))?/);
    if (m) {
        const d = `${m[1]}-${m[2]}-${m[3]}`;
        return isISODate(d) ? `${d} ${(m[4] ?? '00').padStart(2, '0')}:${m[5] ?? '00'}` : null;
    }
    m = s.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})(?:\s+(\d{1,2}):(\d{2}))?/);
    if (!m)
        return null;
    const y = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
    const d = `${y}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
    return isISODate(d) ? `${d} ${(m[4] ?? '00').padStart(2, '0')}:${m[5] ?? '00'}` : null;
}
const PT_TYPES = { TI: 'FS', II: 'SS', TT: 'FF', IT: 'SF', FS: 'FS', SS: 'SS', FF: 'FF', SF: 'SF' };
/** "13;10", "5TI+2 dias", "4II", "7TT-1d", "12FS+50%"... → lista estruturada. Itens inválidos voltam em `invalid`. */
export function parsePredecessors(raw, hoursPerDay = 8) {
    const s = raw === null || raw === undefined ? '' : String(raw).trim();
    if (!s)
        return { preds: [], invalid: [] };
    let parts = s.split(';');
    if (parts.length === 1 && /^\d+(\s*,\s*\d+)+$/.test(s))
        parts = s.split(',');
    const preds = [];
    const invalid = [];
    for (const raw of parts) {
        const p = raw.trim();
        if (!p)
            continue;
        const m = p.match(/^(\d+)\s*(TI|II|TT|IT|FS|SS|FF|SF)?\s*(?:([+-])\s*(.+))?$/i);
        if (!m) {
            invalid.push(p);
            continue;
        }
        let lagHours = 0;
        if (m[3] && m[4]) {
            const h = /%$/.test(m[4].trim()) ? null : parseDurationHours(m[4], hoursPerDay);
            if (h === null) {
                invalid.push(p);
                continue;
            }
            lagHours = m[3] === '-' ? -h : h;
        }
        preds.push({ id: Number(m[1]), type: PT_TYPES[(m[2] ?? 'TI').toUpperCase()] ?? 'FS', lagHours });
    }
    return { preds, invalid };
}
const norm = (s) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
const HEAD = {
    id: ['id'], name: ['nome da tarefa', 'nome', 'task name', 'name'], duration: ['duracao', 'duration'], calendar: ['calendario', 'calendar'],
    critical: ['critica', 'critical'], start: ['inicio', 'start'], finish: ['termino', 'finish'], preds: ['predecessoras', 'predecessors'],
    succs: ['sucessoras', 'successors'], percent: ['% concluida', '% complete', '% conclusao']
};
const DATE_RE = /^(?:[a-zçáéíóú]{3}\s+)?\d{1,2}\/\d{1,2}\/\d{2,4}(?:\s+\d{1,2}:\d{2})?$/i;
/**
 * Lê a tabela impressa pelo MS Project (Arquivo > Imprimir > PDF) a partir dos textos e posições de cada página.
 * O nível de cada tarefa vem do recuo do nome; nomes quebrados em várias linhas são juntados.
 */
export function parseProjectPrint(pages) {
    const warnings = [];
    const tasks = [];
    let title = '';
    const raws = [];
    let headerSeen = false;
    pages.forEach((page, pi) => {
        const items = page.items.filter(i => i.str && i.str.trim());
        // linhas: mesmo y com tolerância (o nome costuma ficar 0,3 pt abaixo das outras colunas)
        const rows = [];
        for (const it of [...items].sort((a, b) => b.y - a.y || a.x - b.x)) {
            const r = rows.find(r => Math.abs(r.y - it.y) <= 2);
            if (r)
                r.items.push(it);
            else
                rows.push({ y: it.y, items: [it] });
        }
        rows.sort((a, b) => b.y - a.y);
        rows.forEach(r => r.items.sort((a, b) => a.x - b.x));
        const hi = rows.findIndex(r => { const t = r.items.map(i => norm(i.str)); return t.some(x => HEAD.name.includes(x)) && t.some(x => HEAD.duration.includes(x) || HEAD.start.includes(x)); });
        if (hi < 0)
            return;
        headerSeen = true;
        if (!title && pi === 0)
            title = rows.slice(0, hi).map(r => r.items.map(i => i.str.trim()).join(' ')).join(' ').replace(/\s+/g, ' ').trim();
        const head = {};
        for (const it of rows[hi].items) {
            const t = norm(it.str);
            for (const [k, al] of Object.entries(HEAD))
                if (al.includes(t) && head[k] === undefined)
                    head[k] = it.x;
        }
        // segunda linha do cabeçalho (ex.: "da tarefa") é ignorada
        const nameX = head.name ?? 0;
        const durX = head.duration ?? Infinity;
        const predX = head.preds;
        const succX = head.succs;
        const idMaxX = nameX - 1;
        let last = null;
        for (const row of rows.slice(hi + 1)) {
            const txt = row.items.map(i => i.str.trim()).join(' ');
            if (/^(p[aá]gina|page)\s+\d+/i.test(txt)) {
                last = null;
                continue;
            }
            const idItem = row.items.find(i => i.x < idMaxX && /^\d+$/.test(i.str.trim()));
            const nameItems = row.items.filter(i => i.x >= nameX - 4 && i.x < durX - 2 && !(idItem && i === idItem));
            if (!idItem) {
                // continuação do nome (ou das predecessoras) da tarefa anterior
                if (last && last.page === pi && last.lastY - row.y <= 12.5 && row.y < last.lastY) {
                    const nm = nameItems.map(i => i.str.trim()).join(' ').trim();
                    if (nm)
                        last.name += ' ' + nm;
                    if (predX !== undefined) {
                        const pc = row.items.filter(i => i.x >= predX - 3 && (succX === undefined || i.x < succX - 3)).map(i => i.str.trim()).join('');
                        if (pc)
                            last.preds += (last.preds.endsWith(';') || !last.preds ? '' : ';') + pc;
                    }
                    last.lastY = row.y;
                }
                continue;
            }
            const rest = row.items.filter(i => i !== idItem && !nameItems.includes(i));
            const dates = rest.filter(i => DATE_RE.test(i.str.trim()));
            const dur = rest.find(i => i.x < (head.calendar ?? head.start ?? Infinity) - 2 && parseDurationHours(i.str) !== null && !DATE_RE.test(i.str.trim()));
            const cal = head.calendar !== undefined ? rest.find(i => Math.abs(i.x - head.calendar) < 3 && !/^(sim|n[aã]o|yes|no)$/i.test(i.str.trim())) : undefined;
            const pct = rest.find(i => /^\d+\s*%$/.test(i.str.trim()));
            const preds = predX !== undefined ? rest.filter(i => i.x >= predX - 3 && (succX === undefined || i.x < succX - 3) && !DATE_RE.test(i.str.trim())).map(i => i.str.trim()).join('') : '';
            last = {
                id: Number(idItem.str.trim()), nameX: nameItems[0]?.x ?? nameX, name: nameItems.map(i => i.str.trim()).join(' ').trim(),
                duration: dur?.str.trim() ?? '', start: dates[0]?.str.trim() ?? '', finish: dates[1]?.str.trim() ?? '', preds, percent: pct?.str ?? '', calendar: cal?.str.trim() ?? '', lastY: row.y, page: pi
            };
            raws.push(last);
        }
    });
    if (!headerSeen)
        return { title, tasks, warnings: ['Não encontrei a tabela do MS Project neste PDF. Ele precisa ter as colunas Nome da tarefa, Duração, Início e Término.'], source: 'pdf' };
    // recuo → nível
    const xs = [...new Set(raws.map(r => Math.round(r.nameX * 2) / 2))].sort((a, b) => a - b);
    const clusters = [];
    for (const x of xs)
        if (!clusters.length || x - clusters[clusters.length - 1] >= 4)
            clusters.push(x);
    const gaps = clusters.slice(1).map((x, i) => x - clusters[i]);
    const step = gaps.length ? Math.min(...gaps) : 11;
    const x0 = clusters[0] ?? 0;
    const seen = new Set();
    for (const r of raws) {
        if (seen.has(r.id)) {
            warnings.push(`Id ${r.id} aparece mais de uma vez; mantive a primeira.`);
            continue;
        }
        seen.add(r.id);
        const { preds, invalid } = parsePredecessors(r.preds);
        if (invalid.length)
            warnings.push(`Tarefa ${r.id}: predecessora não reconhecida (${invalid.join(', ')}).`);
        tasks.push({
            id: r.id, level: Math.max(1, Math.round((r.nameX - x0) / step) + 1), name: r.name.replace(/\s+/g, ' ').trim(),
            start: parseDateTime(r.start), finish: parseDateTime(r.finish), hours: parseDurationHours(r.duration), preds,
            percent: r.percent ? Number(r.percent.replace('%', '')) : null, calendar: r.calendar || null
        });
    }
    for (const t of tasks)
        t.milestone = t.hours === 0;
    return { title, tasks, warnings, source: 'pdf' };
}
/* ------------------------------------------------------------ plano final */
export const IMPORT_WBS_LEVELS = ['Empreendimento', 'Unidade / área', 'Sistema / subsistema', 'Pacote de trabalho'];
const day = (dt) => (dt ? dt.slice(0, 10) : null);
/** término às 00:00 pertence ao dia anterior */
const finishDay = (dt) => {
    if (!dt)
        return null;
    if (dt.slice(11) === '00:00') {
        const d = new Date(dt.slice(0, 10) + 'T00:00:00Z');
        d.setUTCDate(d.getUTCDate() - 1);
        return d.toISOString().slice(0, 10);
    }
    return dt.slice(0, 10);
};
export function buildImportPlan(tasks, calendar = {}, opts = {}) {
    const hpd = opts.hoursPerDay ?? 8;
    const warnings = [];
    const list = tasks.filter(t => t.name.trim());
    if (!list.length)
        throw new Error('O arquivo não tem tarefas.');
    // tarefa-resumo = próxima linha com nível maior
    const isSummary = new Map(list.map((t, i) => [t.id, (list[i + 1]?.level ?? 0) > t.level]));
    const parent = new Map();
    const stack = [];
    for (const t of list) {
        while (stack.length && stack[stack.length - 1].level >= t.level)
            stack.pop();
        parent.set(t.id, stack.length ? stack[stack.length - 1].id : null);
        stack.push(t);
    }
    // resumos recolhidos: tarefa folha seguida de um salto no Id
    const collapsed = [];
    list.forEach((t, i) => { const nx = list[i + 1]; if (!isSummary.get(t.id) && nx && nx.id > t.id + 1 && (t.hours ?? 0) > 3 * hpd)
        collapsed.push(t.id); });
    // a última fase recolhida não tem linha seguinte; vale o irmão que também foi recolhido
    for (const t of list)
        if (!isSummary.get(t.id) && !collapsed.includes(t.id) && (t.hours ?? 0) > 3 * hpd && list.some(o => collapsed.includes(o.id) && parent.get(o.id) === parent.get(t.id)))
            collapsed.push(t.id);
    if (collapsed.length)
        warnings.push(`As linhas ${collapsed.join(', ')} parecem tarefas-resumo recolhidas na impressão (o Id pula depois delas). Entraram como uma atividade cada. Para trazer as subtarefas, imprima com todos os níveis expandidos ou use o XML do MS Project.`);
    // códigos: EAP pela numeração de tópicos, atividades pelo Id do MS Project
    const outline = new Map();
    const counters = [];
    for (const t of list) {
        counters.length = t.level;
        counters[t.level - 1] = (counters[t.level - 1] ?? 0) + 1;
        for (let i = 0; i < t.level - 1; i++)
            counters[i] = counters[i] ?? 1;
        outline.set(t.id, counters.map(c => String(c).padStart(2, '0')).join('.'));
    }
    const maxId = Math.max(...list.map(t => t.id));
    const pad = Math.max(3, String(maxId).length);
    const actCode = (id) => String(id).padStart(pad, '0');
    const wbsAncestor = (id) => { let p = parent.get(id) ?? null; while (p !== null && !isSummary.get(p))
        p = parent.get(p) ?? null; return p; };
    const wbs = list.filter(t => isSummary.get(t.id)).map(t => {
        const p = wbsAncestor(t.id);
        const depth = outline.get(t.id).split('.').length;
        return { code: outline.get(t.id), name: t.name, level: IMPORT_WBS_LEVELS[Math.min(depth, IMPORT_WBS_LEVELS.length) - 1], parentCode: p !== null ? outline.get(p) : null, startDate: day(t.start), finishDate: finishDay(t.finish), sourceId: t.id };
    });
    const leaves = list.filter(t => !isSummary.get(t.id));
    const noDates = leaves.filter(t => !t.start);
    if (noDates.length)
        warnings.push(`${noDates.length} tarefa(s) sem data de início: ${noDates.slice(0, 8).map(t => t.id).join(', ')}. Elas começam no início do projeto.`);
    const starts = leaves.map(t => day(t.start)).filter(Boolean);
    const finishes = leaves.map(t => finishDay(t.finish) ?? day(t.start)).filter(Boolean);
    const projectStart = [...starts].sort()[0] ?? new Date().toISOString().slice(0, 10);
    const cal = new WorkCalendar(calendar);
    cal.ensure(projectStart, [...finishes].sort().at(-1) ?? projectStart);
    const workStart = (t) => cal.idx(day(t.start) ?? projectStart);
    const workEnd = (t) => { const f = finishDay(t.finish) ?? day(t.start) ?? projectStart; return Math.max(workStart(t), cal.idxPrev(f)) + 1; };
    /** posição de um marco na convenção do cálculo: com predecessora ele fica no fim do dia (ES = EF = dia + 1) */
    const isMs = (t) => !!t.milestone || t.hours === 0;
    const msPos = (t) => { const d = day(t.start) ?? projectStart; return t.preds.length ? cal.idxPrev(d) + 1 : cal.idx(d); };
    const leafIds = (id) => {
        if (!isSummary.get(id))
            return [id];
        const i = list.findIndex(t => t.id === id);
        const lvl = list[i].level;
        const out = [];
        for (let j = i + 1; j < list.length && list[j].level > lvl; j++)
            if (!isSummary.get(list[j].id))
                out.push(list[j].id);
        return out;
    };
    const byId = new Map(list.map(t => [t.id, t]));
    let summaryLinks = 0;
    const missing = new Set();
    // vínculos em tarefas-resumo passam para as atividades dentro delas
    const inherited = new Map(leaves.map(t => [t.id, []]));
    for (const t of list) {
        for (const p of t.preds) {
            if (!byId.has(p.id)) {
                missing.add(p.id);
                continue;
            }
            const succs = leafIds(t.id);
            const preds = leafIds(p.id);
            if (isSummary.get(t.id) || isSummary.get(p.id))
                summaryLinks++;
            for (const s of succs)
                for (const pr of preds)
                    if (s !== pr)
                        inherited.get(s).push({ ...p, id: pr });
        }
    }
    if (missing.size)
        warnings.push(`Predecessoras que não estão no arquivo foram ignoradas: ${[...missing].slice(0, 10).join(', ')}.`);
    if (summaryLinks)
        warnings.push(`${summaryLinks} vínculo(s) ligados a tarefas-resumo foram passados para as atividades de dentro do resumo.`);
    const acts = leaves.map(t => {
        const milestone = !!t.milestone || t.hours === 0;
        const duration = milestone ? 0 : workEnd(t) - workStart(t);
        const deps = new Map();
        for (const p of inherited.get(t.id)) {
            const pt = byId.get(p.id);
            const explicit = Math.round(p.lagHours / hpd);
            // folga negativa quando o MS Project encaixa a sucessora no mesmo dia (tarefas de poucas horas)
            const sStart = milestone ? msPos(t) : workStart(t);
            const sEnd = milestone ? msPos(t) : workEnd(t);
            const pStart = isMs(pt) ? msPos(pt) : workStart(pt);
            const pEnd = isMs(pt) ? msPos(pt) : workEnd(pt);
            const derived = p.type === 'SS' ? sStart - pStart : p.type === 'FF' ? sEnd - pEnd : p.type === 'SF' ? explicit : sStart - pEnd;
            const lag = t.start && pt.start ? Math.min(explicit, derived) : explicit;
            const prev = deps.get(p.id);
            if (!prev || prev.lag > lag)
                deps.set(p.id, { predecessorCode: actCode(p.id), type: p.type, lag });
        }
        const s = (day(t.start) ?? projectStart);
        return {
            code: actCode(t.id), name: t.name, wbsCode: (() => { const a = wbsAncestor(t.id); return a !== null ? outline.get(a) : null; })(), duration, milestone,
            hoursPlanned: t.hours !== null && !milestone ? Math.round(t.hours * 10) / 10 : null, constraintType: 'ASAP', constraintDate: null,
            dependencies: [...deps.values()], start: cal.date(cal.idx(s)), finish: milestone ? cal.date(cal.idx(s)) : cal.date(workEnd(t) - 1),
            percent: Math.min(100, Math.max(0, t.percent ?? 0)), actualStart: t.actualStart ?? null, actualFinish: t.actualFinish ?? null, sourceId: t.id, collapsed: collapsed.includes(t.id)
        };
    });
    const cyc = findCycle(acts.map(a => ({ id: a.code, code: a.code, dependencies: a.dependencies.map(d => ({ predecessorId: d.predecessorCode, type: d.type, lag: d.lag })) })));
    if (cyc)
        throw new Error(`O cronograma tem um ciclo de dependências envolvendo ${cyc.join(', ')}.`);
    // restrição "não iniciar antes de" onde a rede sozinha anteciparia a tarefa
    const cpmOf = () => runCpm({
        projectStart, statusDate: projectStart, calendar,
        activities: acts.map(a => ({ id: a.code, code: a.code, duration: a.duration, milestone: a.milestone, constraint: a.constraintType, constraintDate: a.constraintDate, dependencies: a.dependencies.map(d => ({ predecessorId: d.predecessorCode, type: d.type, lag: d.lag })) }))
    });
    for (let pass = 0; pass < 4; pass++) {
        const res = cpmOf();
        let changed = false;
        for (const a of acts) {
            const r = res.items.get(a.code);
            if (r.start < a.start && a.constraintType === 'ASAP') {
                a.constraintType = 'SNET';
                a.constraintDate = a.start;
                changed = true;
            }
        }
        if (!changed)
            break;
    }
    const res = cpmOf();
    let shifted = 0;
    for (const a of acts) {
        const r = res.items.get(a.code);
        if (r.start !== a.start)
            shifted++;
    }
    if (shifted)
        warnings.push(`${shifted} atividade(s) ficaram em datas diferentes das do arquivo por causa do calendário de dias úteis do projeto.`);
    const nonWork = leaves.filter(t => t.calendar && /24|nenhum|none/i.test(t.calendar) && !isSummary.get(t.id)).length;
    if (nonWork)
        warnings.push(`${nonWork} atividade(s) usam calendário corrido (24 horas ou nenhum) no MS Project. Aqui a duração foi contada em dias úteis do projeto.`);
    return {
        projectStart, projectFinish: res.finish, wbs, activities: acts, warnings, collapsed, summaryLinks, shifted,
        dependencyCount: acts.reduce((s, a) => s + a.dependencies.length, 0)
    };
}
//# sourceMappingURL=msproject.js.map